import type { TimelineEvent } from "../../../experience-shell/workflows/timeline";

export interface TimelineDayGroup {
  readonly key: string;
  readonly label: string;
  readonly events: readonly TimelineEvent[];
}

/**
 * Local calendar-day key (`YYYY-MM-DD` from the local getters). The label
 * is local too, so the key and the label always agree. A UTC key would split
 * or merge days wrongly for any viewer off UTC.
 */
export function localDayKey(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function groupByDay(events: readonly TimelineEvent[]): readonly TimelineDayGroup[] {
  const groups: { key: string; label: string; events: TimelineEvent[] }[] = [];
  for (const event of events) {
    const date = new Date(event.occurredAt);
    const valid = !Number.isNaN(date.getTime());
    const key = valid ? localDayKey(date) : "unknown";
    const label = valid ? formatDayLabel(date) : "Unknown date";
    const last = groups.at(-1);
    if (last !== undefined && last.key === key) {
      last.events.push(event);
    } else {
      groups.push({ key, label, events: [event] });
    }
  }
  return groups;
}

function formatDayLabel(date: Date): string {
  const today = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  const diff = startOfDay(today) - startOfDay(date);
  if (diff === 0) return "Today";
  if (diff === oneDay) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
